/**
 * 商品瀑布流组件
 * 基于瀑布流布局实现的商品列表展示组件，支持无限滚动加载、商品分享、购物车操作等功能
 */

// API相关导入
// import { isInGray } from "@/api";
import { SharePosterParams } from "@/api/scode/poster";

// 资源和组件导入
import { ReactComponent as Empty } from "@/assets/empty.svg";
import loadingPng from "@/base64";
import InfiniteScrollContent from "@/components/InfiniteScrollContent";
import PosterSwipe, { CustomEditData } from "@/components/poster-swiper";
import SizePopup from "@/components/size-popup";

// 模型和工具函数导入
import { sharePosterParams4H5Product, useActions } from "@/utils/actions";
import { useCommonContext } from "@/utils/context/common";
import { getCurrentVcScene } from "@/utils/sceneProvider";
import { showSKUPreview } from "@/utils/select-color";
import useNavigation from "@/utils/useNavigate";
import { ProductFeedListCardModel, ProductListItemModel, TProductFeedItem } from "@akc/biz-api";

// UI组件导入
import { ProductCard12Comp } from "@akc/mx-taro-h5-components";
import { InfiniteScroll, Skeleton } from "antd-mobile";

// React相关导入
import { ProductModel, ProductSKUModel } from "@/models/product";
import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { useUnactivate } from "react-activation";
import ReactDOM from "react-dom";
import { useSearchParams } from "react-router-dom";
// 自定义组件导入
import AddPurchasePanel from "../add-purchase-panel";
import Waterfall from "../waterfall";
import styles from "./index.module.less";

// 创建场景上下文，用于在组件树中传递场景信息
const vcSceneContext = React.createContext<string>("");
export const VcSceneProvider = vcSceneContext.Provider;

/**
 * 商品瀑布流组件属性接口
 */
type Props = {
  // 商品列表数据
  productList: Array<TProductFeedItem>;
  // 是否有更多数据可加载
  hasNext: boolean;
  // 加载更多数据的函数
  loadMore: () => Promise<void>;
  // 购物车变化时的回调函数
  onCartChange?: () => Promise<void> | void;
  // 点击商品时的回调函数，(已处理跳转，只需埋点)
  onClickProduct?: (item: ProductListItemModel) => void;
  // 点击购物车按钮时的回调函数（已处理加购弹窗，只需埋点）
  onClickCart?: (item: ProductListItemModel) => void;
  // 点击分享按钮时的回调函数
  onClickShare?: (item: ProductListItemModel) => void;
  // 点击商品属性详情时的回调函数
  onPropertyDetail?: (item: ProductListItemModel) => void;
  // 商卡右上角位置标签code
  topRightLocationCode?: string;
  // 商卡标题前位置标签code
  titleFrontLocationCode?: string;
  // 商卡标题底部位置标签code
  titleBottomLocationCode?: string;
  // 外侧是否有侧边栏，影响每个商品卡片展示的sku数量，有侧边栏时skuCount为3，否则为4
  hasSidebar?: boolean;
  // 卡片是否有边框
  cardHasBorder?: boolean;
  // 瀑布流是否展示more背景色，默认是(如要自定义背景色，设置provideBackground为false，然后通过wraperStyle传入样式)
  provideBackground?: boolean;
  // 瀑布流容器样式
  wraperStyle?: CSSProperties;
  // 是否手动加载更多（显示“查看更多”按钮）
  isManualLoadMore?: boolean;
  // 是否有外边距
  isOuterMargin?: boolean;
  // 埋点数据
  trackData?: any;
  // 按钮点击名称（用于埋点）
  btnClickName?: string;
  // 模态框可见性变化时的回调函数，任意弹窗展示为true，所有弹窗隐藏为false
  onModalVisibleChange?: (visible: boolean) => void;
  // 自定义渲染函数，用于渲染个性化卡片，普通商卡外部无需处理，直接返回null即可，交给组件内渲染
  customChildRender?: (item: TProductFeedItem, index: number) => React.ReactNode | null;
};

/**
 * 商品瀑布流组件（内部已经集成了分享海报弹窗）
 * 用于展示商品列表，支持瀑布流布局、无限滚动加载、商品分享、购物车操作等功能
 */
const ProductWaterfall: React.FC<Props> = ({
  productList,
  hasNext,
  loadMore,
  onCartChange,
  onClickProduct,
  onClickCart,
  onClickShare,
  onPropertyDetail,
  topRightLocationCode,
  titleFrontLocationCode,
  titleBottomLocationCode,
  hasSidebar = false,
  cardHasBorder = false,
  provideBackground = true,
  wraperStyle = {},
  isManualLoadMore = false,
  isOuterMargin = false,
  trackData,
  btnClickName,
  onModalVisibleChange,
  customChildRender,
}) => {
  // 分享海报数据
  const [sharePosterData, setSharePosterData] = useState<SharePosterParams>();
  // 自定义编辑数据状态
  const [customEditData, setCustomEditData] = useState<CustomEditData>();
  // 是否显示转发海报
  const [showPoster, setShowPoster] = useState(false);
  // 从公共上下文中获取设置购物车数量的函数
  const { setCartNum } = useCommonContext();
  // 瀑布流容器DOM高度状态
  const [waterfallContainerDomHeight, setWaterfallContainerDomHeight] = useState<number>(0);
  // 获取URL搜索参数
  const [search] = useSearchParams();
  // 获取自定义页面代码
  const pageCode = search.get("customPageCode");
  // 是否为OTC（非处方药）商品状态
  const [isOTC, setIsOTC] = useState(false);
  // 图片查看器是否可见状态
  const [isImageViewerVisible, setIsImageViewerVisible] = useState(false);
  // 获取场景上下文
  const vcScene = React.useContext(vcSceneContext);
  // 场景引用，优先使用上下文中的场景，否则获取当前场景
  const vcSceneRef = useRef<string>(vcScene || getCurrentVcScene());

  /**
   * 获取当前平台信息的函数
   * 用于判断当前运行环境（小程序、App、H5等）
   * @returns 包含各种平台标识的对象
   */
  const getPlatformFunc = () => {
    // eslint-disable-next-line no-underscore-dangle
    const isMini = window.__wxjs_environment === "miniprogram";
    const isApp = navigator && navigator.userAgent.search("AKC") > -1;
    const isSaas = isMini && !!location.href.match(/plt=saas/i);
    const isWxH5 = !isMini && navigator && !!navigator.userAgent.match(/miniProgram/i);
    const isH5 = isWxH5 || (!isMini && !isApp);
    const isTuan = isMini && location.href.match(/plt=atuan/i);
    return {
      isMini,
      isApp,
      isWxH5,
      isH5,
      isSaas,
      isBrowser: !isMini && !isApp && !isWxH5,
      isTuan,
    };
  };

  const platform = getPlatformFunc();
  const { shareProductPosterAction } = useActions();
  /**
   * 点推荐商品的分享图标
   * @param item
   */
  const forwardProduct = (item: any) => {
    if (!getPlatformFunc()?.isApp && !getPlatformFunc()?.isMini) {
      // H5
      setSharePosterData(
        sharePosterParams4H5Product({
          pageCode: pageCode as string,
          activitySpuId: item.activitySpuId,
          selectSkuId: item.selectSkuId,
        }),
      );
      setCustomEditData({
        activitySpuId: item.activitySpuId,
        activityId: item.activityId,
        selectSkuId: item.selectSkuId,
      });
      setIsOTC(!!item?.skuExtendInfoVO?.isOTC);
      setShowPoster(true);
    } else {
      shareProductPosterAction({
        product: item,
        pageCode: pageCode as string,
      });
    }
    onClickShare?.(item as ProductListItemModel);
  };

  // const forwardProduct = async (item: any) => {
  //   setSharePosterData(
  //     // TODO: pageCode
  //     await sharePosterParams4H5Product({
  //       pageCode: "",
  //       activitySpuId: item.activitySpuId,
  //       selectSkuId: item.selectSkuId || undefined,
  //     }),
  //   );
  //   setCustomEditData({
  //     activitySpuId: item.activitySpuId,
  //     activityId: item.activityId,
  //     selectSkuId: item.selectSkuId,
  //   });
  //   setShowPoster(true);
  // };

  const { navigate2Product, navigate2RankScale, navigate2ProductVideoStream } = useNavigation();
  // 跳商品详情页
  const goDetail = (item, _, salePropertyFirstListPicItem?: any) => {
    if (item.isSkipProductVideoStream) {
      navigate2ProductVideoStream({
        activityId: item.activityId,
        activitySpuId: item.activitySpuId,
        vcScene: vcSceneRef.current,
        selectSkuId: item.selectSkuId,
        firstSalePropertyValue: salePropertyFirstListPicItem?.value,
        showVideoBtmGuide: "1",
        showVideoBottomBar: "1",
        hideForwardBtn: "1",
        theme: "light",
      });
      return;
    }
    navigate2Product({
      activityId: item.activityId,
      activitySpuId: item.activitySpuId,
      selectSkuId: item.selectSkuId,
      firstSalePropertyValue: salePropertyFirstListPicItem?.value,
    });
  };
  // 是否显示加购弹窗
  const [cartModalVisible, setCartModalVisible] = useState<boolean>(false);
  // 加购弹窗商品
  const [modalProduct, setModalProduct] = useState<ProductListItemModel>();
  // 点击商品加购
  const onClickAddBtn = (item: ProductListItemModel) => {
    setCartModalVisible(true);
    setModalProduct(item);
    onModalVisibleChange?.(true);
    onClickCart?.(item as ProductListItemModel);
  };
  // 组件失活时关闭加购弹窗
  useUnactivate(() => {
    setCartModalVisible(false);
    onModalVisibleChange?.(false);
  });
  // 是否展示SKU大图预览弹窗
  const [showSizePopup, setShowSizePopup] = React.useState(false);
  // SKU大图预览商品
  const [sizePopupProduct, setSizePopupProduct] = React.useState<ProductListItemModel>();
  const changeCartNum = () => {
    setCartNum().then(num => {
      console.log("购物车数量", num);
      onCartChange?.();
    });
  };

  const waterfallContainerDom = useRef<HTMLDivElement>(null);
  // 重置瀑布流容器高度
  const resetWaterfallContainerDomHeight = (height: number) => {
    setWaterfallContainerDomHeight(height);
  };

  // 瀑布流容器高度清零
  useEffect(() => {
    productList.length === 0 && setWaterfallContainerDomHeight(0);
  }, [productList]);

  const [isLoading, setIsLoading] = useState(false);

  const handlePosterClose = () => {
    setShowPoster(false);
    document.body.style.overflow = "auto";
    document.body.style.touchAction = "";
    document.body.style.pointerEvents = "";
    onModalVisibleChange?.(false);
  };
  // 监听当前是否有展示任意弹窗
  useEffect(() => {
    const isModalVisible = showSizePopup || showPoster || isImageViewerVisible;
    onModalVisibleChange?.(isModalVisible);
  }, [showSizePopup, showPoster, isImageViewerVisible, onModalVisibleChange]);

  return (
    <>
      <div
        className={`${styles.container} ${provideBackground ? styles.provideBackground : ""}`}
        style={wraperStyle}>
        {/* 骨架屏 */}
        {hasNext && productList.length === 0 && (
          <div className={`${styles.skeletonContainer} ${hasSidebar ? styles.hasSidebar : ""}`}>
            {new Array(6).fill(0).map((_, index) => (
              <Skeleton key={index} animated className={styles.cardSkeleton} />
            ))}
          </div>
        )}
        {/* 商品列表 */}
        {productList.length > 0 && (
          <div
            ref={waterfallContainerDom}
            className={`${styles.waterfallConatainer} ${hasSidebar ? styles.hasSidebar : ""} ${
              isOuterMargin ? styles.outerMargin : ""
            }`}>
            <Waterfall
              dataList={productList}
              childRender={(item, index) => {
                // 如果提供了自定义渲染函数,则交给外部渲染（个性化插卡卡片）
                if (customChildRender) {
                  const customChild = customChildRender(item, index);
                  if (customChild) {
                    return customChild;
                  }
                }
                // 普通商卡
                return (
                  <ProductCard12Comp
                    style={{
                      width: hasSidebar ? "3.04rem" : "3.42rem",
                      border: cardHasBorder ? "0.5px solid #f0f0f0" : "none",
                    }}
                    product={item}
                    topRightLocationCode={topRightLocationCode}
                    titleFrontLocationCode={titleFrontLocationCode}
                    titleBottomLocationCode={titleBottomLocationCode}
                    onProdutClick={() => {
                      goDetail(item, index);
                      onClickProduct?.(item);
                    }}
                    onHotRankClick={() => {
                      navigate2RankScale({
                        rankNo: item.skuExtendInfoVO?.categoryHotRankVO?.rankNo,
                      });
                    }}
                    onSkusClick={() => {
                      const productInfo = {
                        salePropertyFirstList: item.salePropertyFirstList,
                        spuInfoVO: {
                          sellOut: item.spuSellOut,
                        },
                        spuId: item.spuId,
                        activityId: item.activityId,
                        activitySpuId: item.activitySpuId,
                        selectSkuId: item.selectSkuId,
                      };
                      showSKUPreview(productInfo as ProductModel).then(result => {
                        // 点击sku小图
                        if (result) {
                          return;
                        }
                        setSizePopupProduct?.(productInfo);
                        setShowSizePopup(true);
                        onModalVisibleChange?.(true);
                      });
                    }}
                    onAddCartClick={() => onClickAddBtn(item)}
                    onForwardClick={forwardProduct}
                    skuCount={hasSidebar ? 3 : 4}
                    trackData={{ ...trackData, ...item?.trackData }}
                    btnClickName={btnClickName ?? "btn_click"}
                  />
                );
              }}
              margin={{ x: 8, y: 8 }}
              resetHeight={resetWaterfallContainerDomHeight}
            />
          </div>
        )}

        {/* 加载更多、已经到底了 */}
        {isManualLoadMore ? (
          <>
            {!isLoading && (
              <div
                className={styles.seeMoreBtn}
                onClick={() => {
                  setIsLoading(true);
                  loadMore().then(() => {
                    setIsLoading(false);
                  });
                }}>
                <span>查看更多</span>
                <img
                  className={styles.icon}
                  src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAAAXNSR0IArs4c6QAAAaBJREFUOE+tlbtKA0EUhr9JcBtJKm1MOgsRVxKIQiAPoSKmsRUfQBK1EEFsNN4QJOKt00aI19cwECGggptCQVKl0E7Rkcmum83FZMVMN+fyzX/mnJ0VNFgyEung830UGAGGkPSUwwQvwA1wiVe7ENnsR226qDXIkD6GEGtAb6PDHDYDKZPiNn/ujLOBEjyE9RUQibIWd0uCXCeXnxfwZRZhLRnWUyCS7jh1da2JXH7WBlplZv6grI6IlOOqfGE14M7FnbUSb+DV+oUMD04Ap62iXfrjCngMTNoJHg8k5yAYhK0NKBSqWZoGMwkIBCC1Cs9PTv+JAt4DfbY1GoXdfXNbKsH0FBiP5l7BNrchFjP3ZxlYXnICH4QMDb4i8NnWrm44OwefvxqqlDhhyru4ANdXFaDkrR6o3AM67O6BzzpHKTUMGB6uJKd34MCqxJ49Bawt+cdZC3UW1ghm+h/qm+JMbAT9HaYyy01pPjYKupMGvx8U7Oiw2QTF3Q12Z6d5n8ViM5g52CqirZ+e3aB2Pg5lle1+vmyl7Xpgnbf9n1/AN5hVqU1s2NT1AAAAAElFTkSuQmCC"
                />
              </div>
            )}
            {isLoading && (
              <div className={styles.loadingMore}>
                <img src={loadingPng} />
              </div>
            )}
          </>
        ) : (
          <div className={styles.infiniteScrollContainer}>
            {waterfallContainerDomHeight > 0 && (
              <InfiniteScroll loadMore={loadMore} hasMore={hasNext} threshold={0}>
                {(hasNext || productList.length > 0) && <InfiniteScrollContent hasMore={hasNext} />}
              </InfiniteScroll>
            )}
          </div>
        )}
        {!hasNext && productList.length === 0 && (
          <div className={styles.emptyContainer}>
            <Empty />
            <div className={styles.text}>未找到相关商品</div>
          </div>
        )}
      </div>

      {/* SKU大图预览弹窗 */}
      {sizePopupProduct &&
        ReactDOM.createPortal(
          <SizePopup
            product={sizePopupProduct as ProductModel}
            show={showSizePopup}
            close={() => {
              setShowSizePopup(false);
              onModalVisibleChange?.(false);
            }}
            toDetailByFirstProp={(product, salePropertyFirstListPicItem) => {
              goDetail(product, undefined, salePropertyFirstListPicItem);
              onPropertyDetail?.(product);
            }}
          />,
          document.body,
        )}

      {/* 转发海报弹窗 */}
      {showPoster &&
        sharePosterData &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 99999,
            }}>
            <PosterSwipe
              show={true}
              posterData={sharePosterData}
              customEditData={customEditData}
              isOTC={isOTC}
              shareType="PRODUCT"
              btnConfig={["copy", "edit"]}
              close={handlePosterClose}
            />
          </div>,
          document.body,
        )}

      {/* 加购弹窗 */}
      <AddPurchasePanel
        visible={cartModalVisible}
        product={modalProduct}
        needLoading={true}
        close={() => {
          setCartModalVisible(false);
          onModalVisibleChange?.(false);
        }}
        confirm={changeCartNum}
        onModalStateChange={visible => {
          setIsImageViewerVisible(visible);
        }}
      />
    </>
  );
};

export default ProductWaterfall;
